import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { toast } from "react-toastify";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faBellSlash, faCheck, faClock, faEye, faGlobe, faHeartPulse, faRotateLeft, faShieldHeart } from "@fortawesome/free-solid-svg-icons";
import AppShell, { PageHeader } from "../components/shell/app-shell";
import Icon3D from "../components/ui/icon-3d";
import Segmented from "../components/ui/segmented";
import SearchInput from "../components/ui/search-input";
import Select from "../components/ui/select";
import SidePanel from "../components/ui/side-panel";
import StatusPill from "../components/ui/status-pill";
import EmptyState from "../components/ui/empty-state";
import SkeletonList from "../components/ui/skeleton-list";
import { Fields } from "../components/ui/detail";
import { Tone } from "../components/ui/tones";
import TimeAgo from "../components/time-ago";
import useQueryState, { useDebouncedQueryState, usePageParam } from "../hooks/use-query-state";
import ListFooter from "../components/pagination/list-footer";
import useAutoRefresh from "../hooks/use-auto-refresh";
import { platformParam, usePlatform } from "../store/use-platform";
import { AlertAction, AlertItem, AlertView, fetchAlerts, updateAlert } from "../utils/api";
import { platformLabel } from "../utils/status";
import { formatDateTime } from "../utils/format-time";
import { ParseErrors } from "../utils/ParseErrors";

const SOURCE_LOOK: Record<AlertItem["source"], { label: string; icon: IconDefinition; tone: Tone; hint: string }> = {
  api: { label: "API", icon: faGlobe, tone: "sky", hint: "A route keeps failing. Open API logs to see the requests." },
  cron: { label: "Cron", icon: faClock, tone: "violet", hint: "A scheduled job failed, crashed, never started or is stuck." },
  health: { label: "Health", icon: faHeartPulse, tone: "emerald", hint: "A server or one of its services didn't pass its check." },
};

const ACTIONS: Record<AlertAction, { label: string; done: string; icon: IconDefinition }> = {
  acknowledge: { label: "Acknowledge", done: "Acknowledged", icon: faEye },
  mute: { label: "Mute", done: "Muted", icon: faBellSlash },
  resolve: { label: "Resolve", done: "Resolved", icon: faCheck },
  reopen: { label: "Reopen", done: "Reopened", icon: faRotateLeft },
};

// ----------------------- WHICH ACTIONS FIT THE CURRENT STATE ------------------------------------
const PAGE_SIZE = 25;

const actionsFor = (state: AlertItem["state"]): AlertAction[] => {
  if (state === "open") return ["acknowledge", "mute", "resolve"];
  if (state === "acknowledged") return ["mute", "resolve"];
  return ["reopen"];
};

// ----------------------- WHERE TO LOOK NEXT ------------------------------------
const linkFor = (alert: AlertItem) => {
  if (alert.source === "api") return { to: `/logs?search=${encodeURIComponent(String(alert.ref.route ?? "").replace(/^route:[A-Z]+ /, ""))}`, label: "Open the matching API logs" };
  if (alert.source === "cron") return { to: `/cron-jobs?job=${alert.ref.jobId}`, label: "Open the job and its runs" };
  return { to: `/health?kind=${alert.ref.kind}`, label: "Open health checks" };
};

function ActionButtons({ alert, onAction, compact = false }: { alert: AlertItem; onAction: (action: AlertAction) => void; compact?: boolean }) {
  return (
    <>
      {actionsFor(alert.state).map((action) => (
        <button
          key={action}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onAction(action);
          }}
          className={clsx(
            "inline-flex items-center gap-1.5 rounded-xl border border-edge bg-surface font-[Gilroy-Medium] text-light-gray transition-colors hover:border-accent hover:text-heading-color",
            compact ? "h-8 px-2.5 text-[12px]" : "h-10 px-3.5 text-[13px]",
          )}
        >
          <FontAwesomeIcon icon={ACTIONS[action].icon} className="text-[11px]" />
          {ACTIONS[action].label}
        </button>
      ))}
    </>
  );
}

export default function AlertsPage() {
  const { platform } = usePlatform();
  const [view, setView] = useQueryState<string>("view", "open");
  const [source, setSource] = useQueryState<string>("source", "");
  const [severity, setSeverity] = useQueryState<string>("severity", "");
  const [search, searchInput, setSearchInput] = useDebouncedQueryState("search");
  const [page, setPage] = usePageParam();
  const [total, setTotal] = useState(0);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [counts, setCounts] = useState<Record<AlertView, number>>({ open: 0, muted: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AlertItem | null>(null);
  const [leaving, setLeaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await fetchAlerts({ view, source, severity, search, platform: platformParam(platform), page, limit: PAGE_SIZE });
      setAlerts(result.data);
      setTotal(result.totalCount);
      setCounts(result.counts);
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setLoading(false);
    }
  }, [view, source, severity, search, platform, page]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);
  useAutoRefresh(load, 30000);

  const act = async (alert: AlertItem, action: AlertAction) => {
    try {
      setLeaving(alert.id);
      await updateAlert(alert.id, action);
      toast.success(ACTIONS[action].done);
      setSelected(null);
      await load();
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setLeaving(null);
    }
  };

  return (
    <AppShell>
      <PageHeader title="Alerts" subtitle="One inbox for failing APIs, cron jobs that went wrong, and servers that failed their pulse or health check." />

      <div className="relative z-20 mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <Segmented
          label="Alert state"
          value={view}
          onChange={setView}
          options={[
            { label: "Open", value: "open", count: counts.open },
            { label: "Muted", value: "muted", count: counts.muted },
            { label: "Resolved", value: "resolved", count: counts.resolved },
          ]}
        />
        <div className="flex flex-wrap gap-2">
          {(["", "api", "cron", "health"] as const).map((item) => {
            const look = item ? SOURCE_LOOK[item] : null;
            const on = source === item;
            return (
              <button
                key={item || "all"}
                type="button"
                aria-pressed={on}
                onClick={() => setSource(item)}
                className={clsx(
                  "flex h-10 items-center gap-2 rounded-xl border pl-1.5 pr-3.5 font-[Gilroy-Medium] text-[13px] transition-all",
                  on ? "border-accent bg-active-background text-heading-color" : "border-edge bg-surface text-light-gray hover:border-edge-strong",
                  !look && "pl-3.5",
                )}
              >
                {look ? <Icon3D icon={look.icon} tone={look.tone} size="sm" flat className="!h-7 !w-7 !rounded-lg !text-[12px]" /> : null}
                {look?.label ?? "All sources"}
              </button>
            );
          })}
        </div>
        <div className="flex flex-1 gap-2 lg:justify-end">
          <Select label="Severity" value={severity} onChange={setSeverity} options={[{ label: "All", value: "" }, { label: "Critical", value: "critical", dot: "bg-rose-500" }, { label: "Warning", value: "warning", dot: "bg-amber-500" }]} />
        </div>
      </div>
      <div className="mb-5 flex"><SearchInput value={searchInput} onChange={setSearchInput} placeholder="Search alerts" /></div>

      {loading ? (
        <SkeletonList rows={5} height="h-24" />
      ) : !alerts.length ? (
        <div className="glass">
          <EmptyState icon={faShieldHeart} tone="emerald" title={view === "open" ? "All clear" : `No ${view} alerts`} text={view === "open" ? "No open problems right now. New ones appear here within a minute." : "Nothing here yet."} />
        </div>
      ) : (
        <ul className="stagger space-y-3">
          {alerts.map((alert) => {
            const look = SOURCE_LOOK[alert.source];
            const critical = alert.severity === "critical";
            return (
              <li key={alert.id} className={clsx("transition-all duration-300", leaving === alert.id && "translate-x-6 opacity-0")}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelected(alert)}
                  onKeyDown={(event) => event.key === "Enter" && setSelected(alert)}
                  className={clsx("glass glass-hover flex cursor-pointer flex-col gap-4 p-4 sm:flex-row sm:items-center", critical && alert.state === "open" && "border-rose-500/40")}
                >
                  <div className="flex min-w-0 flex-1 items-start gap-4">
                    <Icon3D icon={look.icon} tone={critical ? "rose" : "amber"} size="md" flat />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusPill tone={critical ? "rose" : "amber"} live={critical && alert.state === "open"}>{critical ? "Critical" : "Warning"}</StatusPill>
                        <span className="font-[Gilroy-Medium] text-[12px] text-label-muted">{look.label} · {platformLabel(alert.platform)}</span>
                        {alert.state === "acknowledged" ? <StatusPill tone="sky">Acknowledged</StatusPill> : null}
                      </div>
                      <p className="mt-1.5 truncate font-[Gilroy-Bold] text-[15px] text-heading-color">{alert.title}</p>
                      <p className="mt-0.5 truncate font-[Gilroy-Medium] text-[13px] text-light-gray">{alert.detail}</p>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2 sm:flex-col sm:items-end">
                    <span className="font-[Gilroy-Medium] text-[12px] text-label-muted">
                      {alert.count > 1 ? `${alert.count.toLocaleString()}× · ` : ""}
                      <TimeAgo value={alert.lastSeen} />
                    </span>
                    <div className="flex gap-1.5"><ActionButtons alert={alert} compact onAction={(action) => act(alert, action)} /></div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ListFooter page={page} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} />

      <SidePanel
        open={!!selected}
        onClose={() => setSelected(null)}
        icon={selected ? <Icon3D icon={SOURCE_LOOK[selected.source].icon} tone={selected.severity === "critical" ? "rose" : "amber"} size="lg" /> : null}
        title={selected?.title}
        subtitle={selected ? `${SOURCE_LOOK[selected.source].label} alert · ${platformLabel(selected.platform)}` : undefined}
        badge={selected ? <StatusPill tone={selected.severity === "critical" ? "rose" : "amber"} live={selected.state === "open"}>{selected.severity === "critical" ? "Critical" : "Warning"} · {selected.state}</StatusPill> : null}
        footer={selected ? <ActionButtons alert={selected} onAction={(action) => act(selected, action)} /> : null}
      >
        {selected ? (
          <>
            <div className="rounded-2xl border border-edge bg-overlay p-4 font-[Gilroy-Medium] text-[14px] text-heading-color">
              <p>{selected.detail}</p>
              <p className="mt-2 text-[13px] text-light-gray">{SOURCE_LOOK[selected.source].hint}</p>
            </div>
            <Fields
              rows={[
                ["Platform", platformLabel(selected.platform)],
                ["Times seen", selected.count.toLocaleString()],
                ["First seen", formatDateTime(selected.firstSeen)],
                ["Last seen", <TimeAgo value={selected.lastSeen} />],
                ...(Object.entries(selected.ref).filter(([, value]) => value != null).map(([key, value]) => [key, <span className="font-mono text-[12px]">{String(value)}</span>]) as [string, JSX.Element][]),
              ]}
            />
            <Link to={linkFor(selected).to} className="glass glass-hover flex items-center justify-between p-4 font-[Gilroy-Medium] text-[14px] text-blue">
              {linkFor(selected).label}
              <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </>
        ) : null}
      </SidePanel>
    </AppShell>
  );
}
